/**
 * @file   src\components\Onboarding\Dashboard\PropertyListing.js
 * @brief  This file is responsible for showing each want ad in want ad listing page
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ShareIcon from "../../../../images/share-icon.svg";
import LakeViewIcon from "../../../../images/lake-view-icon.svg";
import HouseTagIcon from "../../../../images/property-icon-house.svg";
import MatchReasonsPopover from "@/components/MatchReasonsPopover";
import {
  loggedInUser,
  getLocalStorage,
  formateDate,
  durationForFlexibleDate,
  monthNameForFlexibleDate,
  getUserId,
  removeDecimalZeros,
  getUserType,
  getAddressDisplay,
  thousandSeperatedValue,
} from "../../../../helpers/common";
import { useSearchParams } from "react-router-dom";
import {
  ADTYPE_STR,
  IN_CITY_LIMITS,
  RURAL,
  USER_INFO_KEY,
  LOT_LAND,
  USER_TYPE_AGENT,
} from "../../../../constants/common";
import AddToClientWantAdModal from "../../../MemberWantAd/AddToClientWantAdModal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { setLotSizeValue } from "../../../../helpers/createWantAd";
import { getWantAdById } from "../../../../services/wantAdService";
import DefaultProfile from "../../../../images/v2/Profiles.svg";
import { getCombinedText } from "../../../../helpers/getPropertyIcons";
import ShareYourAdModal from "../../../MemberWantAd/v2/ShareYourAdModal";
import { useSelector } from "react-redux";
import PremiumPlanModal from "../../../PlanUpgrade/PremiumPlanModal";
import BasicPlanModal from "../../../../components/PlanUpgrade/BasicPlanModal";
import { formatThousandSeparatedValue } from "@/helpers";
import useChipVisibleCount from "@/hooks/useChipVisibleCount";
import { useAppConfig } from "@/appConfig";

export const emptyPropertyPlaceholderImages = [
  'house-empty-placeholder-01.png',
  'house-empty-placeholder-02.png',
  'house-empty-placeholder-03.png',
  'house-empty-placeholder-04.png',
]

const PropertyListing = ({
  handleFavoriteOrUnfavoriteWantAd,
  onChangeText,
  onAddClient,
  handleInventoryClick,
  ...props
}) => {
  const [verifyUser, setVerifyUser] = useSearchParams();
  const isUserVerified = verifyUser.get("verify");
  const [isFavourite, setIsFavourite] = useState(0);
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const [modalShow, setModalShow] = useState(false); //
  const user = loggedInUser();
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const {
    Bedrooms,
    score,
    Bathrooms,
    Sqft,
    n2h,
    isLotLand,
    LotType_Rural_City,
    Location_Img,
    photo,
    LotSz_mn,
    LotSz_mx,
    AdType,
    Inv_Id,
    wantAdId,
    Location,
    City,
    State,
    subPropertyTypeId,
    Date_From,
    AskingPrice_Rent,
    AskingPrice_Sell,
    Date_To,
    DateType,
    Duration,
    Month,
    Longitude,
    Latitude,
    mapRadius,
    userFirstName,
    userLastName,
    userAvatar,
    userCompany,
    Is_Agent_Created,
    slug,
    ZIP,
    aiExplanation,
    aiHighlights,
    aiBonus,
    aiProcessed,
    // MLS listing fields
    Is_MLS_Listing,
    MLS_Standard_Status,
    MLS_Listing_URL,
    MLS_ListAgent_Name,
    MLS_ListOffice_Name,
    reasons,
  } = props && props.list;

  const isMlsListing = Is_MLS_Listing === 1 || Is_MLS_Listing === true;

  const emptyPlaceholderImage = emptyPropertyPlaceholderImages[Math.floor(Math.random() * emptyPropertyPlaceholderImages.length)];
  const emptyPlaceholderImageUrl = require(`../../../../images/${emptyPlaceholderImage}`);

  const invPhoto = photo || emptyPlaceholderImageUrl;


  const [showAiInsights, setShowAiInsights] = useState(false);
  const [showShareModal, setShareModal] = useState(false);
  const handleClose = () => setShareModal(false);
  const [selectedWantAd, setSelectedWantAd] = useState({});
  const loggedInUserId = getUserId();
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const [userDetails, setUserDetails] = useState(null);

  const { flags } = useAppConfig();

  const price = AskingPrice_Rent || AskingPrice_Sell;

  const userType = getUserType();
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      setUserDetails(userDetailsFromStore[0]);
    } else {
      setUserDetails(null);
    }
  }, [userDetailsFromStore]);

  const settings = {
    className: "slider variable-width",
    dots: false,
    infinite: false,
    centerMode: false,
    slidesToShow: 1,
    swipeToSlide: true,
    variableWidth: true,
    arrows: false,
  };

  /**
   * To display must haves in want ad tile
   * @returns
   */
  const loadMustHaves = () => {
    if (isLotLand) {
      if (LotType_Rural_City) {
        return (
          <div className="property-details">
            {LotType_Rural_City === 1 ? RURAL : IN_CITY_LIMITS}{" "}
            <span className="details-text">lot type</span>
            <span className="vertical-divider">|</span>
            {setLotSizeValue(
              removeDecimalZeros(LotSz_mn),
              removeDecimalZeros(LotSz_mx),
            )}{" "}
            {setLotSizeValue(
              removeDecimalZeros(LotSz_mn),
              removeDecimalZeros(LotSz_mx),
            ) === "Any" ? (
              <span className="details-text">lot size</span>
            ) : (
              <></>
            )}
          </div>
        );
      } else {
        return (
          <div className="property-details">
            {setLotSizeValue(
              removeDecimalZeros(LotSz_mn),
              removeDecimalZeros(LotSz_mx),
            )}{" "}
            {setLotSizeValue(
              removeDecimalZeros(LotSz_mn),
              removeDecimalZeros(LotSz_mx),
            ) === "Any" ? (
              <span className="details-text">lot size</span>
            ) : (
              <></>
            )}
          </div>
        );
      }
    } else {
      let bedRm;
      let bathRm;

      let timeLine;
      if (Bedrooms) {
        if (Bedrooms === 99) {
          bedRm = "Any";
        } else {
          bedRm = `${parseInt(Bedrooms)}+`;
        }
      }

      if (Bathrooms) {
        if (Bathrooms === 99) {
          bathRm = "Any";
        } else {
          bathRm = `${parseInt(Bathrooms)}+`;
        }
      }

      if (AdType === ADTYPE_STR) {
        if (DateType === 1 || DateType == null) {
          timeLine =
            formateDate(Date_From, "mm/dd/yyyy") +
            " - " +
            formateDate(Date_To, "mm/dd/yyyy");
        } else if (DateType === 2) {
          timeLine =
            durationForFlexibleDate(Duration) +
            " - " +
            monthNameForFlexibleDate(Month);
        }
      }

      return (
        <div className="property-details">
          {bedRm && (
            <>
              {bedRm} <span className="details-text">beds</span>
            </>
          )}
          {bathRm && (
            <>
              <span className="vertical-divider">|</span>
              {bathRm} <span className="details-text">baths</span>
            </>
          )}
          {Sqft ? <span className="vertical-divider">|</span> : ""}
          {Sqft && AdType !== ADTYPE_STR && (
            <>
              <span>{formatThousandSeparatedValue(Sqft)}</span>{" "}
              <span className="details-text"> sqft</span>
            </>
          )}
          {timeLine && AdType === ADTYPE_STR && (
            <>
              <span className="details-text">{timeLine}</span>
            </>
          )}
        </div>
      );
    }
  };

  /**
   * To get want ad details for share want ad
   * @param {*} wantAdId
   */
  const getWantAdDetails = async (wantAdId) => {
    if (wantAdId) {
      await getWantAdById(wantAdId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            setSelectedWantAd(responseResult);
            setShareModal(true);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  /**
   * handle share want ad functionality
   * @param {*} e
   * @param {*} wantAdId
   */
  const handleShare = (e, wantAdId) => {
    e.stopPropagation();
    getWantAdDetails(wantAdId);
  };

  /**
   * To show property type icons on map image
   * @param {*} props
   * @returns
   */
  const renderTooltip = (props) => (
    <Tooltip
      id="button-tooltip"
      placement="bottom-start"
      className="custom-tooltip"
      {...props}
    >
      {subPropertyTypeId &&
        subPropertyTypeId?.length > 0 &&
        getCombinedText(subPropertyTypeId)}
    </Tooltip>
  );

  const maxVisibleN2H = useChipVisibleCount();

  const loadNiceToHaveList = () => {
    if (!n2h) return null;
    const visible = n2h.slice(0, maxVisibleN2H);
    const overflow = n2h.length - maxVisibleN2H;
    const items = visible.map((nh, index) => {
      const id = _.get(nh, "N2H_Id");
      const itemText = _.get(nh, "Text", "");
      const icon = _.get(nh, "Icon", "");
      return (
        <div
          className="inner-slider-wrap"
          key={index + "_" + _.get(nh, "Want_ad_Id", "")}
        >
          <div className="property-tag mb-0">
            <div className="tag-icon">
              <img
                src={
                  icon ? require("../../../../images/" + icon) : LakeViewIcon
                }
              />
            </div>
            <span>{itemText}</span>
          </div>
        </div>
      );
    });
    if (overflow > 0) {
      items.push(
        <div className="inner-slider-wrap" key="n2h-overflow">
          <span className="n2h-overflow-count">+{overflow}</span>
        </div>
      );
    }
    return items;
  };

  const { before, after } = getAddressDisplay(Location, City, State, ZIP);

  // MLS listings open in a new tab to the agent's MLS system
  const handleCardClick = () => {
    if (isMlsListing && MLS_Listing_URL) {
      window.open(MLS_Listing_URL, "_blank", "noopener,noreferrer");
    } else {
      handleInventoryClick(Inv_Id, slug);
    }
  };

  return (
    <>
      <a
        role="button"
        className="property-listing-link"
        href={isMlsListing && MLS_Listing_URL ? MLS_Listing_URL : void 0}
        target={isMlsListing ? "_blank" : undefined}
        rel={isMlsListing ? "noopener noreferrer" : undefined}
        onClick={(e) => {
          if (!isMlsListing) {
            e.preventDefault();
            handleCardClick();
          }
        }}
      >
        <div className={`property-listing match-card${isMlsListing ? " mls-match-card" : ""}`}>
          <div className={`property-type-wrap`}>
            <OverlayTrigger
              placement="bottom-start"
              delay={{ show: 250, hide: 400 }}
              overlay={renderTooltip}
            >
              <div
                className={`property-tile-matches`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {
                  <>
                    <img
                      src={HouseTagIcon}
                      alt={HouseTagIcon}
                      className="property-icon-img"
                    />
                  </>
                }
                <p style={{ marginTop: 0, marginBottom: 0 }}>For sale</p>
              </div>
            </OverlayTrigger>
            {isMlsListing && MLS_Standard_Status && (
              <div className={`mls-status-badge mls-status-${MLS_Standard_Status === "Coming Soon" ? "coming-soon" : "active"}`}>
                {MLS_Standard_Status === "Coming Soon" ? "Coming Soon" : "MLS"}
              </div>
            )}
          </div>

          <div className="property-img-wrap h-200">
            <img src={invPhoto} alt="Map Image" loading="lazy" decoding="async" />
          </div>

          <div className="property-info-outer">
            <div className="property-top">
              <div className="property-info-wrap">
                <div className="price-container">
                  <div className="prize-range-badge">
                    ${thousandSeperatedValue(Math.ceil(price))}
                  </div>

                  {score != null && (
                    <MatchReasonsPopover reasons={reasons} score={score}>
                      <span
                        className="match-score-text"
                        data-tour="match-score-badge"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {score}% Match
                      </span>
                    </MatchReasonsPopover>
                  )}
                </div>

                <h6 className="long-txt-fix">
                  {before}
                  {after ? (
                    <>
                      {" "}
                      <span className="dot-color long-txt-fix">
                        &bull;
                      </span>{" "}
                      <span className="long-txt-fix">{after}</span>
                    </>
                  ) : null}
                </h6>
                {loadMustHaves()}

                {/* AI Match Insights - collapsible section */}
                {aiExplanation && (
                  <div className="ai-match-insights">
                    <button
                      className="ai-insights-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAiInsights(!showAiInsights);
                      }}
                    >
                      <span className="ai-insights-icon">&#9679;</span>
                      {showAiInsights ? "Hide insights" : "Why this match"}
                      <span className={`ai-insights-chevron ${showAiInsights ? "open" : ""}`}>&#8250;</span>
                    </button>
                    {showAiInsights && (
                      <div className="ai-insights-content">
                        {aiHighlights && aiHighlights.length > 0 && (
                          <ul className="ai-insights-highlights">
                            {aiHighlights.map((h, i) => (
                              <li key={i}>{h}</li>
                            ))}
                          </ul>
                        )}
                        <p className="ai-insights-explanation">{aiExplanation}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div
                className="social-btns-wrap rmt-12"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {user && (
                  <a
                    role="button"
                    className="social-content-btn mr-12"
                    href={void 0}
                    onClick={(e) => handleShare(e, wantAdId)}
                  >
                    <img src={ShareIcon} />
                  </a>
                )}
              </div>
            </div>
            <div className="property-btm">
              {n2h?.length > 0 && (
                <div
                  className="sliderwrapper"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Slider {...settings}>{loadNiceToHaveList()}</Slider>
                </div>
              )}
              <div className="property-agent-info">
                {isMlsListing ? (
                  <MlsAgentInfo
                    agentName={MLS_ListAgent_Name}
                    officeName={MLS_ListOffice_Name}
                  />
                ) : Is_Agent_Created ? (
                  <>
                    <div className="property-agent-profile-wrap">
                      <img
                        src={userAvatar || DefaultProfile}
                        alt="avatar"
                        className="property-agent-profile"
                        width={"35px"}
                        height={"35px"}
                      />
                    </div>
                    <div className="property-agent-details">
                      <div className="property-agent-name">
                        Listed by {userFirstName} {userLastName}
                        {flags.proBadge ? (
                          <span className="pro-tag">PRO</span>
                        ) : null}
                      </div>
                      <div className="property-agent-requested-infoo">
                        {`Agent @ ${userCompany}`}
                      </div>
                    </div>
                  </>
                ) : (
                  <span className="property-not-agent-name">
                    {AdType === 1 ? "Buyer" : "Renter"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </a>
      {/*  Add to client favorite Modal */}
      <AddToClientWantAdModal
        show={modalShow}
        showAddToClientWantAdModal={() => setModalShow(true)}
        onHide={() => setModalShow(false)}
        data={props.results}
        handleSkip={() =>
          handleFavoriteOrUnfavoriteWantAd(isFavourite, wantAdId)
        }
        // handleConfirm={handleClientFavoriteWantAd}
        searchText={props.clientSearchText}
        onChangeText={(e) => onChangeText(e)}
        onAddClient={() => onAddClient()}
      />
      {/** Plan Modal */}
      {userDetails && userDetails?.userType == 1 ? (
        <PremiumPlanModal
          userDetails={userDetails}
          show={showPremiumPlanModal}
          onHide={PremiumPlanModalClose}
          showPlanModal={PremiumPlanModalShow}
        />
      ) : (
        <BasicPlanModal
          userDetails={userDetails}
          show={showPremiumPlanModal}
          onHide={PremiumPlanModalClose}
          showPlanModal={PremiumPlanModalShow}
        />
      )}
      {showShareModal && (
        <ShareYourAdModal
          show={showShareModal}
          onHide={handleClose}
          adDetails={selectedWantAd}
          isWantAdCreated={false}
          AdType={_.get(selectedWantAd, "adType", "") === "BUY" ? 1 : 2}
          isAdTypeIsLand={_.get(selectedWantAd, "prprtyType", "") === 6}
          adTypeBasedOnProperty={
            _.get(selectedWantAd, "prprtyType", "") === 6
              ? LOT_LAND
              : _.get(selectedWantAd, "adType", "")
          }
        />
      )}
    </>
  );
};
export default PropertyListing;

const HiddenAgentInfo = () => {
  return (
    <>
      <div className="property-agent-profile-wrap">
        <img
          src={DefaultProfile}
          alt="avatar"
          className="property-agent-profile"
          width={"35px"}
          height={"35px"}
        />
      </div>
      <div className="property-not-agent-name">Agent</div>
    </>
  );
};

// MLS listing agent info - shows listing agent name and brokerage from MLS data
const MlsAgentInfo = ({ agentName, officeName }) => {
  return (
    <div className="property-agent-details mls-agent-info">
      {agentName && (
        <div className="property-agent-name">
          Listed by {agentName}
        </div>
      )}
      {officeName && (
        <div className="property-agent-requested-infoo">
          {officeName}
        </div>
      )}
      {!agentName && !officeName && (
        <div className="property-not-agent-name">MLS Listing</div>
      )}
    </div>
  );
};
